import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Dropdown from 'rc-dropdown';
import styled from '@emotion/styled';
import { useTheme } from 'emotion-theming';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import { FOCUS_FILL_VAR, focusRing } from '../../../../shared/theme/focusStyles';
import themedScrollbar from './themedScrollbar';
import { claimArrowKeys } from '../../utils/arrowKeyOwnership';
import { setIsCalendarOptionsDropdownMenuOpen } from '../../ducks/calendarActions';
import {
  OPTION_CONTROL_VERTICAL_PADDING,
  OPTION_CONTROL_LINE_HEIGHT,
  OPTION_CONTROL_FONT_SIZE,
  OPTION_CONTROL_FONT_WEIGHT,
} from './ExerciseOptionPill';
import 'rc-dropdown/assets/index.css';

/** The "Options" dropdown that sits at the head of the transposed table's
 * status pill row. A second, closer-to-hand surface for the same toggles the
 * three-dot "Additional Options" menu owns - both run the same `toggle` from
 * useExerciseOptionToggles, so they can't drift apart.
 *
 * Built on rc-dropdown to match the three-dot menu: rc-trigger portals the
 * popup to document.body and flips it when it would run off screen, which this
 * needs because the table header sits inside a horizontally scrolling
 * container that would otherwise clip an absolutely positioned menu.
 *
 * Keyboard operation follows the module's house pattern (see
 * StaticAddColumnDropdownSimple and CalendarItemDropdownOptions): the rows are
 * never tab stops or focus targets, and `focusedIndex` is a keyboard cursor
 * moved by a window listener. DOM focus can't drive it - rc-trigger portals the
 * popup to document.body and mounts its children a commit later, so focus would
 * have nothing to land on when the arrow keys start. Focus instead sits on the
 * `role='menu'` wrapper, which reports the current row through
 * `aria-activedescendant`. */
const TriggerButton = styled('button')`
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
  /** Same padding and line height as ExerciseOptionPill so the trigger and the
   * pills beside it come out exactly the same height. */
  padding: ${OPTION_CONTROL_VERTICAL_PADDING} 14px;
  border: none;
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.buttonPrimaryBg};
  font-family: 'Nunito Sans';
  font-size: ${OPTION_CONTROL_FONT_SIZE};
  line-height: ${OPTION_CONTROL_LINE_HEIGHT};
  font-weight: ${OPTION_CONTROL_FONT_WEIGHT};
  color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
  &:disabled {
    background: ${({ theme }) => theme.colors.buttonDisabledBg};
    color: ${({ theme }) => theme.colors.buttonDisabledText};
    cursor: default;
  }
  ${focusRing}
`;

/** Panel styling mirrors the program tag filter dropdown in EntryPointContent so
 * the two read as the same control. That one splits scrolling onto an inner list
 * because its search bar has to stay pinned; with no search bar here the scroll
 * and its thin scrollbar sit on the wrapper itself. */
const MenuWrapper = styled('div')`
  min-width: 220px;
  max-height: 300px;
  overflow-y: auto;
  padding: 8px;
  background: ${({ theme }) => theme.colors.surfaceElevated};
  border: 2px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  /** This takes DOM focus so aria-activedescendant has an owner, but it is not
   * itself the indicator - the highlighted row is. global.scss strips outlines
   * app-wide anyway, so nothing renders here regardless. */
  &:focus {
    outline: none;
  }

  ${themedScrollbar}
`;

/** `greyed` and `isFocused` are styling-only props (emotion keeps them off the
 * DOM). `isFocused` is the keyboard cursor, not DOM focus. */
const OptionRow = styled('div')`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 4px;
  font-family: 'Nunito Sans';
  font-size: 14px;
  color: ${({ greyed, theme }) => (greyed ? theme.colors.textDisabled : theme.colors.text)};
  text-align: left;
  cursor: ${(props) => (props.greyed ? 'not-allowed' : 'pointer')};
  /** Two cursors, two colours: grey for the mouse, FOCUS_FILL for the keyboard.
   * The keyboard cursor wins where they collide, or a pointer left resting
   * anywhere in the menu would paint over the row Enter is about to toggle. A
   * greyed row still takes the fill when it is the cursor - it stays inert, but
   * the coach has to be able to see where they are. */
  background: ${(props) => (props.isFocused ? FOCUS_FILL_VAR : 'transparent')};
  &:hover {
    background: ${(props) => {
    if (props.isFocused) return FOCUS_FILL_VAR;
    return props.greyed ? 'transparent' : props.theme.colors.surfaceSecondary;
  }};
  }
`;

/** The tag filter draws this check off a visually-hidden `input:checked`; these
 * rows are `menuitemcheckbox`/`aria-checked` instead, which is the right pattern
 * inside a menu, so the same `::after` hangs off the `checked` prop. */
const CheckBox = styled('div')`
  position: relative;
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid ${({ theme }) => theme.colors.link};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.surface};
  opacity: ${(props) => (props.greyed ? 0.5 : 1)};

  ${(props) => props.checked && `
    &::after {
      content: '';
      position: absolute;
      left: 5px;
      top: 1px;
      width: 5px;
      height: 10px;
      border: solid ${props.theme.colors.link};
      border-width: 0 2px 2px 0;
      transform: rotate(45deg);
    }
  `}
`;

/** aria-activedescendant points at a row by id, and those ids have to be unique
 * document-wide: a day renders dozens of these menus, and react-beautiful-dnd
 * renders a second copy of the whole row as the drag clone. React 16 has no
 * useId, and content.assignedId is identical on the row and its clone - a
 * per-instance counter is the only thing that is genuinely unique. */
let menuInstanceCount = 0;

const ExerciseOptionsMenu = ({ options }) => {
  const { colors } = useTheme();
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  /** The keyboard cursor. Not DOM focus - see the component docblock. */
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [isTriggerFocused, setIsTriggerFocused] = useState(false);
  const calendarContainerScrollPosition = useSelector(
    (state) => state.calendar.data.calendarContainerScrollPosition,
  );

  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const instanceIdRef = useRef(null);
  if (instanceIdRef.current === null) {
    menuInstanceCount += 1;
    instanceIdRef.current = menuInstanceCount;
  }
  const menuId = `exercise-options-menu-${instanceIdRef.current}`;
  const rowId = (index) => `${menuId}-row-${index}`;
  const blockedTooltipId = `${menuId}-blocked-tooltip`;

  const visibleOptions = options.filter((option) => option.isAvailable);

  /** The key handlers read the rows and the cursor through refs rather than
   * closing over them, so the listeners attach once per open instead of once
   * per render. That matters more here than in the other menus:
   * useExerciseOptionToggles is deliberately unmemoized, so `options` is a
   * fresh array every render, and the useSelector above re-renders this
   * component on every calendar scroll frame. */
  const visibleOptionsRef = useRef(visibleOptions);
  visibleOptionsRef.current = visibleOptions;
  const focusedIndexRef = useRef(focusedIndex);
  focusedIndexRef.current = focusedIndex;

  /** Focus lives inside the portaled menu while it is open, so closing it has
   * to hand focus back rather than strand the coach on <body>. Guarded because
   * a close racing an unmount has nothing to hand it to. */
  const closeAndReturnFocus = () => {
    setIsOpen(false);
    if (menuRef.current && menuRef.current.contains(document.activeElement)) {
      if (triggerRef.current) triggerRef.current.focus();
    }
  };

  /** The popup is portaled to the body, so it would otherwise float detached
   * from its (virtualized) row once the calendar scrolls or the window
   * resizes. Mirrors CalendarItemDropdownOptions. */
  useEffect(() => {
    if (!isOpen) return undefined;
    window.addEventListener('resize', closeAndReturnFocus);
    return () => window.removeEventListener('resize', closeAndReturnFocus);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) closeAndReturnFocus();
  }, [calendarContainerScrollPosition]);

  /** The row wrappers flush the item's pending save on any mousemove outside
   * the row, and this popup is portaled outside it - so without this every
   * toggle PUT on its own instead of batching in the debounce. Same flag the
   * three-dot menu raises. The cleanup also runs on unmount, which a
   * virtualized row scrolling away does routinely; a flag left raised would
   * stop every row flushing. */
  useEffect(() => {
    if (!isOpen) return undefined;
    dispatch(setIsCalendarOptionsDropdownMenuOpen(true));
    return () => dispatch(setIsCalendarOptionsDropdownMenuOpen(false));
  }, [isOpen]);

  /** Own the arrow keys while open, so useCalendarKeyboardScroll leaves the
   * calendar where it is. That scroll dispatches
   * calendarContainerScrollPosition, which the effect above reads as the coach
   * scrolling and closes on - the menu would shut itself on its own navigation
   * keypress.
   *
   * The claim starts as soon as the trigger takes focus, not when the menu
   * opens, because ArrowDown-to-open has the same problem and is a commit too
   * early to fix from an `isOpen` effect: the scroll hook and this component's
   * handler both hang off `document`, and whichever registered first wins. By
   * the time any key can reach a focused trigger the claim is already held, so
   * the ordering stops mattering. It costs nothing - a focused menu button owns
   * the arrows anyway.
   *
   * The cleanup also covers unmount, which a virtualized row scrolling away
   * does routinely; a leaked claim would disable calendar scrolling app-wide. */
  useEffect(() => {
    if (!isOpen && !isTriggerFocused) return undefined;
    return claimArrowKeys();
  }, [isOpen, isTriggerFocused]);

  useEffect(() => {
    if (isOpen) setFocusedIndex(0);
  }, [isOpen]);

  /** rc-util's Portal creates its container in a layout effect, so on the very
   * first open the overlay's children mount a commit after this one and the ref
   * is still null here. A macrotask lands after that commit - the same trick
   * the superset popover in PreSaveTableTransposed uses.
   *
   * Focus has to actually sit on the wrapper for aria-activedescendant to mean
   * anything: it only reports the current row of the element that HAS focus. It
   * cannot go on the trigger, because aria-activedescendant is a
   * composite-role property and jsx-a11y rejects it on a <button>. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const id = setTimeout(() => {
      if (menuRef.current) menuRef.current.focus({ preventScroll: true });
    }, 0);
    return () => clearTimeout(id);
  }, [isOpen]);

  /** Moving the cursor and acting on it. Bubble phase: nothing else in play
   * handles these keys, so there is no one to beat. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDown = (e) => {
      const rows = visibleOptionsRef.current;
      const lastIndex = rows.length - 1;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.min(prev + 1, lastIndex));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Home') {
        e.preventDefault();
        setFocusedIndex(0);
      } else if (e.key === 'End') {
        e.preventDefault();
        setFocusedIndex(lastIndex);
      } else if (e.key === 'Enter' || e.key === ' ') {
        /** preventDefault first and unconditionally, before any decision about
         * the row. The trigger is a real <button> and still holds focus, so
         * without this the browser runs its activation behaviour, rc-trigger
         * sees the click, and the menu closes on the very key meant to tick a
         * row. Space would also scroll the page. Cancelling the keydown
         * cancels both - Space activates on keyup, but only if its keydown was
         * not cancelled. */
        e.preventDefault();
        const row = rows[Math.min(focusedIndexRef.current, lastIndex)];
        /** Mirror the click path exactly: a greyed row is inert, and the menu
         * stays open either way so several can be set in one visit. */
        if (row && row.canToggle) row.toggle();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    /** Tearing this down matters: a listener left attached after close would
     * let a later stray Enter re-fire whichever row was last highlighted. */
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  /** Escape and Tab have to be claimed in the window CAPTURE phase. Every other
   * handler in play is a bubble-phase window listener registered earlier:
   * rc-dropdown's useAccessibility (which closes on Escape, and swallows the
   * first Tab entirely by trying to focus the overlay div), and in month view
   * MonthEditPopover, which would collapse the whole exercise row. Same node,
   * earlier registration, so stopPropagation from a bubble handler is always
   * too late. Same precedent as CalendarItemDropdownOptions.
   *
   * Claiming Tab this aggressively is safe only because none of the seven
   * transposed tables render inside a react-modal focus trap - MonthEditPopover
   * is a raw portal, and the day/week/virtualized paths go through
   * DraggableDivWrapper. A future modal host would need to hand Tab back. */
  useEffect(() => {
    if (!isOpen) return undefined;
    const handleKeyDownCapture = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closeAndReturnFocus();
      } else if (e.key === 'Tab') {
        /** No preventDefault, deliberately - focus is inside the menu, so
         * handing it back to the trigger lets the browser carry on from there,
         * forwards on Tab and backwards on Shift+Tab. Closing just stops an
         * orphaned menu being left open behind it. */
        e.stopImmediatePropagation();
        closeAndReturnFocus();
      }
    };
    window.addEventListener('keydown', handleKeyDownCapture, true);
    return () => window.removeEventListener('keydown', handleKeyDownCapture, true);
  }, [isOpen]);

  /** DOM focus never moves row to row, so nothing scrolls the highlighted row
   * into view on its own - and eight rows overflow MenuWrapper's 300px. */
  useEffect(() => {
    if (!isOpen) return;
    const row = document.getElementById(rowId(focusedIndex));
    if (row) row.scrollIntoView({ block: 'nearest' });
  }, [isOpen, focusedIndex]);

  return (
    <Dropdown
      trigger={['click']}
      visible={isOpen}
      onVisibleChange={setIsOpen}
      overlay={(
        /** stopPropagation keeps the menu open across toggles - without it the
         * click reaches rc-trigger's document handler and closes the popup. */
        <MenuWrapper
          ref={menuRef}
          id={menuId}
          role='menu'
          tabIndex={-1}
          aria-label='Exercise options'
          aria-activedescendant={rowId(focusedIndex)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.stopPropagation();
            /** Also keep the press from blurring the wrapper. Focus never moves
             * into the rows themselves - the wrapper holds it and reports the
             * current row through aria-activedescendant - so a mouse toggle
             * that dropped focus to <body> would leave the arrow keys pointing
             * at nothing. */
            e.preventDefault();
          }}
        >
          {visibleOptions.map((option, index) => {
            const greyed = !option.canToggle;
            /** Only a row blocked by another option explains itself - the
             * other greyed states have no reason worth surfacing. */
            const tooltipReason = option.isBlocked ? option.blockedReason : null;
            return (
              <OptionRow
                key={option.id}
                id={rowId(index)}
                role='menuitemcheckbox'
                /** A menu is one stop in the tab order and its rows are reached
                 * with the arrow keys, so these are never tab stops. */
                tabIndex={-1}
                aria-checked={option.isChecked}
                aria-disabled={greyed}
                greyed={greyed}
                /** Greyed rows are deliberately NOT skipped by the arrow keys,
                 * unlike the three-dot menu's isGreyedOut rows. The row is the
                 * explanation for why an option is locked, so arrowing past it
                 * hides that; and canToggle flips while the menu is open (Bodyweight
                 * locks the moment Completion Only is ticked), so a nav list
                 * filtered on it would shift every index below the row the coach
                 * just acted on. */
                isFocused={index === focusedIndex}
                onClick={greyed ? undefined : option.toggle}
                data-tooltip-id={tooltipReason ? blockedTooltipId : undefined}
                data-tooltip-content={tooltipReason || undefined}
              >
                <CheckBox checked={option.isChecked} greyed={greyed} />
                {option.label}
              </OptionRow>
            );
          })}
          {/** Inside the overlay so the anchor resolves within rc-dropdown's
            * portal. Styled to match SwapPropDropdown's blocked tooltip. */}
          <ReactTooltip
            id={blockedTooltipId}
            place='right'
            positionStrategy='fixed'
            style={{
              zIndex: 9999,
              backgroundColor: 'rgba(68, 68, 68, 1)',
              color: 'white',
              fontFamily: 'Nunito Sans',
              fontSize: '13px',
            }}
          />
        </MenuWrapper>
      )}
    >
      <TriggerButton
        ref={triggerRef}
        type='button'
        aria-haspopup='menu'
        aria-expanded={isOpen}
        aria-controls={isOpen ? menuId : undefined}
        disabled={!visibleOptions.length}
        /** The header row lives inside the react-beautiful-dnd drag handle, so
         * a press that reaches it would start a phantom drag. Keeping this a
         * real <button> is what makes the keyboard path safe too: rbd's
         * keyboard sensor refuses to start a drag from an interactive element. */
        onMouseDown={(e) => e.stopPropagation()}
        onTouchStart={(e) => e.stopPropagation()}
        /** Focus here is what claims the arrow keys - see the effect above. */
        onFocus={() => setIsTriggerFocused(true)}
        onBlur={() => setIsTriggerFocused(false)}
        onKeyDown={(e) => {
          /** Enter and Space are deliberately absent. This is a real <button>,
           * so the browser already synthesizes the click rc-trigger opens on;
           * opening here as well would set visible=true and then let that click
           * toggle it straight back shut. (StaticAddColumnDropdownSimple only
           * needs them because its trigger is a div.)
           *
           * The arrows have no activation behaviour of their own, so they are
           * the only keys this has to handle. Both open on the first row - the
           * list is eight rows at most, and a second "where does the cursor
           * start" rule would earn nothing. */
          if (isOpen) return;
          if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(true);
        }}
      >
        Options
        <IcomoonReact
          iconSet={iconSet}
          size={8}
          icon={isOpen ? 'uparrowalt2' : 'downarrowalt2'}
          color={visibleOptions.length ? colors.white : colors.buttonDisabledText}
        />
      </TriggerButton>
    </Dropdown>
  );
};

ExerciseOptionsMenu.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    label: PropTypes.string,
    isChecked: PropTypes.bool,
    isAvailable: PropTypes.bool,
    canToggle: PropTypes.bool,
    isBlocked: PropTypes.bool,
    blockedReason: PropTypes.string,
    toggle: PropTypes.func,
  })).isRequired,
};

export default ExerciseOptionsMenu;
